import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CheckboxTree from '../../components/connectedForm/CheckboxTree';
import getIn from '../../components/connectedForm/utils/getIn';
import { useDispatch } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { ICustomerContact, ICustomerGlobalContact, ISupplierContact } from '../../graphql/schema/p4types';
import { cloneDeep } from '@apollo/client/utilities';

const CUSTOMER_DEFAULTS = {
  receiveAcknowledgements: 'Acknowledgement',
  receiveConformityCertificates: 'Conformity Certificate',
  receiveDataSheets: 'Data Sheet',
  receiveDeliveryNotes: 'Delivery Note',
  receiveDispatchEmails: 'Dispatch Email',
  receiveDirectDebitLetters: 'Direct Debit Letter',
  receiveInvoiceDocuments: 'Invoice',
  receiveProformaInvoiceDocuments: 'Proforma Invoice',
  receiveQuotationDocuments: 'Quotation Document',
  receiveStatementDocuments: 'Statement',
  receivePaymentReceipts: 'Payment Receipts',
  receiveCreditDocuments: 'Credit',
  receiveOverdueAccount: 'Overdue Account',
};

const SUPPLIER_DEFAULTS = {
  receivePurchaseOrderDocument: 'Purchase Orders',
  receiveRemittanceDocument: 'Remittance Documents',
  receiveDebitNoteDocument: 'Debit Notes',
};

const initialCheckboxTree = (
  contact: ICustomerContact | ISupplierContact | ICustomerGlobalContact,
  defaults: { [key: `receive${string}`]: string }
) => {
  const checked: Array<any> = [];

  for (const key in defaults) {
    const documentDefault: any = getIn(contact, key);
    if (documentDefault && !documentDefault.deleted) {
      checked.push(defaults[key as keyof typeof defaults]);
    }
  }

  return checked;
};

const DocumentCheckboxTree = ({
  contactid,
  type,
  disabled,
}: {
  contactid: string;
  type: 'customer' | 'supplier';
  disabled: boolean;
}) => {
  //Note - Values may be 'ICustomer' OR 'ICustomerContact' (Latter when editing global contacts).
  const { change, values } = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const contact = contactid.length === 0 ? values : getIn(values, contactid);

  const defaults = useMemo(() => (type === 'customer' ? CUSTOMER_DEFAULTS : SUPPLIER_DEFAULTS), [type]);

  const [checked, setChecked] = useState(
    initialCheckboxTree(contact as ICustomerContact | ISupplierContact | ICustomerGlobalContact, defaults)
  );
  const [expanded, setExpanded] = useState(['documents']);
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
    } else {
      setChecked(
        initialCheckboxTree(contact as ICustomerContact | ISupplierContact | ICustomerGlobalContact, defaults)
      );
    }
  }, [contact, defaults]);

  const onCheck = (checkedArray: Array<any>) => {
    const newContact = cloneDeep(contact);

    for (const key in defaults) {
      newContact[key] = !!checkedArray.find((checked: any) => checked === defaults[key as keyof typeof defaults]);
    }

    dispatch(change(contactid.length > 0 ? contactid : false, newContact));
    setChecked(checkedArray);
  };

  const nodes = useMemo(() => {
    const baseNodes: Array<{ value: string; label: string; children?: Array<any> }> = [
      {
        value: 'documents',
        label: 'All Documents',
      },
    ];

    const children: Array<{ value: any; label: string }> = [];
    for (const key in defaults) {
      const value = defaults[key as keyof typeof defaults];
      children.push({
        value: value,
        label: toTitleCase(value),
      });
    }

    baseNodes[0].children = children;
    return baseNodes;
  }, [defaults]);

  return (
    <CheckboxTree
      disabled={disabled}
      nodes={nodes}
      checked={checked}
      expanded={expanded}
      onCheck={onCheck}
      onExpand={setExpanded}
    />
  );
};

DocumentCheckboxTree.defaultProps = {
  type: 'customer',
  disabled: false,
};

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt: string) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export default DocumentCheckboxTree;
